import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User } from '@/types';
import { authService } from '@/services/authService';
import { secureLogger } from '@/utils/secureLogger';

interface AuthState {
  user: User | null;
  permissions: string[];
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    role?: User['role'];
  }) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  updateProfile: (data: { name?: string; email?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string | string[]) => boolean;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        permissions: [],
        isLoading: true,  // Start as true so ProtectedRoute shows loading state
        error: null,
        isAuthenticated: false,

        // Actions
        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null });
          
          try {
            const result = await authService.login({ email, password });
            const permissions = authService.getUserPermissions();
            
            set({
              user: result.user,
              permissions,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Login failed',
              isLoading: false,
              isAuthenticated: false,
            });
            throw error;
          }
        },

        register: async (data) => {
          set({ isLoading: true, error: null });
          
          try {
            const result = await authService.register(data);
            const permissions = authService.getUserPermissions();
            
            set({
              user: result.user,
              permissions,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Registration failed',
              isLoading: false,
              isAuthenticated: false,
            });
            throw error;
          }
        },

        logout: async () => {
          set({ isLoading: true });
          
          try {
            await authService.logout();
          } catch (error) {
            secureLogger.warn('Logout request failed:', error);
          }
          
          set({
            user: null,
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        },

        logoutAll: async () => {
          set({ isLoading: true });
          
          try {
            await authService.logoutAll();
          } catch (error) {
            secureLogger.warn('Logout all request failed:', error);
          }
          
          set({
            user: null,
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        },

        updateProfile: async (data) => {
          set({ isLoading: true, error: null });
          
          try {
            const updatedUser = await authService.updateProfile(data);
            
            set({
              user: updatedUser,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Profile update failed',
              isLoading: false,
            });
            throw error;
          }
        },

        changePassword: async (currentPassword: string, newPassword: string) => {
          set({ isLoading: true, error: null });
          
          try {
            await authService.changePassword({
              currentPassword,
              newPassword,
            });
            
            set({
              isLoading: false,
              error: null,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Password change failed',
              isLoading: false,
            });
            throw error;
          }
        },

        refreshProfile: async () => {
          try {
            const user = await authService.refreshProfile();
            const permissions = authService.getUserPermissions();
            
            set({
              user,
              permissions,
              isAuthenticated: true,
            });
          } catch (error) {
            set({
              user: null,
              permissions: [],
              isAuthenticated: false,
              error: error instanceof Error ? error.message : 'Failed to refresh profile',
            });
            throw error;
          }
        },

        clearError: () => {
          set({ error: null });
        },

        checkAuth: async () => {
          set({ isLoading: true });
          
          try {
            // First check if we have any stored user data (quick check without API calls)
            const storedUser = localStorage.getItem('user');
            if (!storedUser) {
              set({
                user: null,
                permissions: [],
                isAuthenticated: false,
                isLoading: false,
              });
              return;
            }

            // Only proceed with token validation if we have stored user data
            if (!authService.isAuthenticated()) {
              set({
                user: null,
                permissions: [],
                isAuthenticated: false,
                isLoading: false,
              });
              return;
            }

            const user = authService.getCurrentUser();
            const permissions = authService.getUserPermissions();

            if (user) {
              set({
                user,
                permissions,
                isAuthenticated: true,
                isLoading: false,
              });

              // Refresh profile to ensure data is up to date
              try {
                await get().refreshProfile();
              } catch (error) {
                secureLogger.warn('Failed to refresh profile on auth check:', error);
              }
            } else {
              set({
                user: null,
                permissions: [],
                isAuthenticated: false,
                isLoading: false,
              });
            }
          } catch (error) {
            set({
              user: null,
              permissions: [],
              isAuthenticated: false,
              isLoading: false,
            });
          }
        },

        hasPermission: (permission: string) => {
          return authService.hasPermission(permission);
        },

        hasRole: (role: string | string[]) => {
          return authService.hasRole(role);
        },
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          permissions: state.permissions,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Selectors for commonly used state
export const useAuth = () => {
  const { user, isAuthenticated, isLoading, error } = useAuthStore();
  return { user, isAuthenticated, isLoading, error };
};

export const usePermissions = () => {
  const { permissions, hasPermission, hasRole } = useAuthStore();
  return { permissions, hasPermission, hasRole };
};